import { createContext, useContext } from 'react';
import { useQuery } from '@apollo/client';
import { Center } from '@chakra-ui/react';

import { InsurancesPerks } from '__generated__/GQL';
import useCompanyContext from 'common/hooks/useCompanyContext';
import { FCWithChildren } from 'common/types';
import ErrorCard from 'components/ErrorCard';
import Loader from 'components/Loader';

import { OfferWithLink } from '../../types';
import { InsurancesCoverageDocument } from './insurancesCoverage.gql';
import { InsurancesCoverage } from './types';
import { splitOffers } from './utils';

const InsuranceCoverageContext = createContext<
  | {
      activeOffers: OfferWithLink[];
      coverage: InsurancesCoverage;
      inactiveOffers: OfferWithLink[];
      perks: InsurancesPerks;
    }
  | undefined
>(undefined);

export const InsuranceCoverageProvider: FCWithChildren = ({ children }) => {
  const { companyProfileId = '' } = useCompanyContext();

  const { data, error, loading } = useQuery(InsurancesCoverageDocument, {
    skip: !companyProfileId,
    variables: { companyProfileId },
  });

  if (error) {
    return (
      <Center height="100%">
        <ErrorCard />
      </Center>
    );
  }

  if (!companyProfileId || loading || !data) {
    return <Loader />;
  }

  const { insurances } = data.viewer.company.currentPlan;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { __typename, ...coverage } = insurances.coverage;

  const { activeOffers, inactiveOffers } = splitOffers(coverage);

  return (
    <InsuranceCoverageContext.Provider
      value={{
        activeOffers,
        coverage,
        inactiveOffers,
        perks: insurances.perks,
      }}
    >
      {children}
    </InsuranceCoverageContext.Provider>
  );
};

const useInsuranceCoverage = () => {
  const context = useContext(InsuranceCoverageContext);

  if (!context) {
    throw new Error(
      '"useInsuranceCoverage" can only be used under InsuranceCoverageProvider',
    );
  }

  return context;
};

export default useInsuranceCoverage;
